import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import FractalCanvas from './FractalCanvas';
import MantraOverlay from './MantraOverlay';
import AudioStemMixer from './AudioStemMixer';
import AccessibilityBar from './AccessibilityBar';
import { useAudioEngine } from '../hooks/useAudioEngine';
import { API_URL } from '../../api';
import { fetchAuthSession } from 'aws-amplify/auth';

const PracticePlayer = () => {
    const { deityId, practiceId } = useParams();
    const [practice, setPractice] = useState(null);
    const [scene, setScene] = useState(null);
    const [stems, setStems] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);

    // Audio engine hook
    const { loadStems, play, pause, stop, currentTime, setStemGain, isAudioLoaded } = useAudioEngine();

    // Settings
    const [settings, setSettings] = useState({
        reduceMotion: false,
        showCaptions: true,
        highContrast: false,
        dyslexicFont: false,
        mantraVolume: 0.9,
        ambientVolume: 0.4,
        bellVolume: 0.8
    });

    useEffect(() => {
        const loadPracticeData = async () => {
            try {
                setIsLoading(true);
                setError(null);

                const { tokens } = await fetchAuthSession();
                const token = tokens?.idToken?.toString();
                const headers = {
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: token } : {}),
                };

                // Fetch practice and audio assets in parallel
                const [practicesRes, audioRes] = await Promise.all([
                    fetch(`${API_URL}/temple/practices?deityId=${encodeURIComponent(deityId)}`, { headers }),
                    fetch(`${API_URL}/temple/audio?practiceId=${encodeURIComponent(practiceId)}`, { headers }),
                ]);

                if (!practicesRes.ok) {
                    throw new Error(`Failed to load practices: ${practicesRes.status}`);
                }
                if (!audioRes.ok) {
                    throw new Error(`Failed to load audio assets: ${audioRes.status}`);
                }

                const practicesData = await practicesRes.json();
                const audioData = await audioRes.json();

                // Find this specific practice from the list
                const foundPractice = (practicesData.practices || []).find(p => p.id === practiceId);
                if (!foundPractice) {
                    setPractice(null);
                    setIsLoading(false);
                    return;
                }

                // Map audio assets to stems format the audio engine expects
                const audioAssets = audioData.assets || audioData.audioAssets || [];
                const mappedStems = audioAssets
                    .filter(asset => asset.url)
                    .map(asset => ({
                        id: asset.id,
                        url: asset.url,
                        loop: asset.loop !== undefined ? asset.loop : true,
                        gain: asset.gain !== undefined ? asset.gain : 0.5,
                        type: asset.stemType,
                    }));

                // Parse steps from JSON string if needed
                let steps = [];
                if (foundPractice.steps) {
                    try {
                        steps = typeof foundPractice.steps === 'string'
                            ? JSON.parse(foundPractice.steps)
                            : foundPractice.steps;
                    } catch (e) {
                        console.warn('Could not parse practice steps:', e);
                        steps = [];
                    }
                }

                // Build scene from practice data
                const builtScene = {
                    stems: mappedStems,
                    visuals: {
                        intro: {
                            fractal: 'flame',
                            palette: foundPractice.mood === 'wrathful' ? 'ember' : 'ocean',
                            speed: 0.3,
                            intensity: 0.6
                        },
                        mantra: {
                            fractal: 'kaleidoscope',
                            palette: foundPractice.mood === 'wrathful' ? 'ruby' : 'teal',
                            speed: 0.6,
                            pulseOnBeat: true,
                            intensity: 0.8
                        },
                        dedication: {
                            fractal: 'lotus',
                            palette: 'gold',
                            speed: 0.2,
                            intensity: 0.4
                        }
                    },
                    steps: steps.length > 0 ? steps : [
                        {
                            t: 0,
                            visualKey: 'intro',
                            caption: foundPractice.title,
                            mantra: '',
                            highlight: null
                        }
                    ],
                };

                setPractice(foundPractice);
                setScene(builtScene);
                setStems(mappedStems);
            } catch (err) {
                console.error('Error loading practice data:', err);
                setError('Could not load practice. Please try again.');
            } finally {
                setIsLoading(false);
            }
        };

        loadPracticeData();
    }, [deityId, practiceId]);

    useEffect(() => {
        if (stems.length > 0 && !isAudioLoaded) {
            loadStems(stems);
        }
    }, [stems, isAudioLoaded, loadStems]);

    useEffect(() => {
        if (isPlaying && scene) {
            const timer = setInterval(() => {
                const nextStepIndex = scene.steps.findLastIndex(step => currentTime >= step.t);
                if (nextStepIndex !== -1 && nextStepIndex !== currentStep) {
                    setCurrentStep(nextStepIndex);
                }
            }, 100);

            return () => clearInterval(timer);
        }
    }, [isPlaying, currentTime, scene, currentStep]);

    const handlePlay = () => {
        if (!isPlaying) {
            play();
            setIsPlaying(true);
        } else {
            pause();
            setIsPlaying(false);
        }
    };

    const handleStop = () => {
        stop();
        setIsPlaying(false);
        setCurrentStep(0);
    };

    const toggleFullscreen = () => {
        if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen();
            setIsFullscreen(true);
        } else {
            document.exitFullscreen();
            setIsFullscreen(false);
        }
    };

    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
    }, []);

    if (isLoading) {
        return (
            <div className="min-h-screen bg-black text-white flex items-center justify-center">
                <div className="text-2xl">Loading practice...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-black text-white flex items-center justify-center">
                <div className="text-center space-y-4">
                    <p className="text-red-400 text-lg">{error}</p>
                    <Link to={`/temple/${deityId}`} className="text-blue-400 hover:text-blue-300">
                        Return to Deity
                    </Link>
                </div>
            </div>
        );
    }

    if (!practice || !scene) {
        return (
            <div className="min-h-screen bg-black text-white flex items-center justify-center">
                <div className="text-center">
                    <h1 className="text-2xl mb-4">Practice not found</h1>
                    <Link to="/temple" className="text-blue-400 hover:text-blue-300">
                        Return to Temple
                    </Link>
                </div>
            </div>
        );
    }

    const currentStepData = scene.steps[currentStep] || scene.steps[0];

    return (
        <div className={`${isFullscreen ? 'fixed inset-0 z-50' : 'min-h-screen'} bg-black text-white relative overflow-hidden`}>
            {/* Fractal Background */}
            <div className="absolute inset-0">
                <FractalCanvas
                    theme={practice.mood}
                    params={scene.visuals[currentStepData.visualKey] || scene.visuals.intro}
                    time={currentTime}
                    reduceMotion={settings.reduceMotion}
                />
            </div>

            {/* Accessibility Bar */}
            <AccessibilityBar
                settings={settings}
                onSettingsChange={setSettings}
                className="absolute top-4 left-4 z-20"
            />

            {/* Main Controls */}
            <div className="absolute top-4 right-4 z-20 flex gap-2">
                <button
                    onClick={toggleFullscreen}
                    className="bg-gray-900 bg-opacity-75 hover:bg-opacity-90 px-3 py-2 rounded-lg text-white transition-all"
                    title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
                >
                    {isFullscreen ? '\u229E' : '\u29C1'}
                </button>
                <Link
                    to={`/temple/${deityId}`}
                    className="bg-gray-900 bg-opacity-75 hover:bg-opacity-90 px-3 py-2 rounded-lg text-white transition-all"
                >
                    &times;
                </Link>
            </div>

            {/* Mantra Overlay */}
            {currentStepData.mantra && (
                <MantraOverlay
                    text={currentStepData.mantra}
                    caption={settings.showCaptions ? currentStepData.caption : ''}
                    highlight={currentStepData.highlight}
                    dyslexicFont={settings.dyslexicFont}
                    highContrast={settings.highContrast}
                    className="absolute inset-x-0 top-1/2 transform -translate-y-1/2 z-10"
                />
            )}

            {/* Caption Only (when no mantra) */}
            {!currentStepData.mantra && settings.showCaptions && currentStepData.caption && (
                <div className="absolute bottom-24 left-1/2 transform -translate-x-1/2 z-10">
                    <div className="bg-black bg-opacity-75 px-6 py-3 rounded-lg">
                        <p className={`text-center text-lg ${settings.highContrast ? 'text-yellow-300' : 'text-white'}`}>
                            {currentStepData.caption}
                        </p>
                    </div>
                </div>
            )}

            {/* Audio Controls */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-20">
                <div className="flex items-center gap-4 bg-gray-900 bg-opacity-75 px-6 py-3 rounded-lg">
                    <button
                        onClick={handlePlay}
                        disabled={!isAudioLoaded}
                        className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 px-4 py-2 rounded-lg font-semibold transition-colors"
                    >
                        {isPlaying ? 'Pause' : 'Play'}
                    </button>
                    <button
                        onClick={handleStop}
                        disabled={!isAudioLoaded}
                        className="bg-gray-600 hover:bg-gray-700 disabled:bg-gray-500 px-4 py-2 rounded-lg font-semibold transition-colors"
                    >
                        Stop
                    </button>

                    {/* Time Display */}
                    <div className="text-sm text-gray-300 min-w-16">
                        {Math.floor(currentTime / 60)}:{String(Math.floor(currentTime % 60)).padStart(2, '0')}
                    </div>
                </div>
            </div>

            {/* Audio Stem Mixer */}
            <AudioStemMixer
                stems={stems}
                onGainChange={setStemGain}
                settings={settings}
                onSettingsChange={setSettings}
                className="absolute bottom-4 right-4 z-20"
            />
        </div>
    );
};

export default PracticePlayer;
